import React, { useEffect, useState } from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Cpu, Sparkles, DollarSign, Lock, Coins } from 'lucide-react';
import { usePW } from '@/lib/store';
import { modelsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { pricePer10K, pricePer500K, fmtUsd, DETECTION_SCORES, BEST_USE } from '@/lib/cost';
import { providerHandle, providerTagline } from '@/lib/providerLabel';
import WordCreditBuyDialog from '../WordCreditBuyDialog';

const PREMIUM_METERED_IDS = ['claude-opus-4-7', 'gpt-5-4'];

export function ModelPicker() {
  const { project, updateProject } = usePW();
  const [models, setModels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const currentId = project?.model_id || 'claude-sonnet-4-5';
  const active = models.find((m) => m.id === currentId);

  useEffect(() => {
    let cancelled = false;
    modelsApi.list()
      .then((r) => { if (!cancelled) setModels(r.models || []); })
      .catch(() => { /* offline ok; static fallback */ });
    return () => { cancelled = true; };
  }, []);

  const pick = async (id) => {
    if (!project || id === currentId) return;
    setBusy(true);
    try {
      await updateProject({ model_id: id });
      toast.success(`Model switched to ${models.find((m) => m.id === id)?.label || id}.`);
      // iter55f85 — Analytics: record which model each tier prefers.
      try {
        const { trackChatModelSelected } = await import('@/lib/analytics');
        trackChatModelSelected(id);
      } catch { /* silent */ }
    } catch (e) {
      toast.error(`Could not update model: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const families = models.reduce((acc, m) => {
    (acc[m.family] = acc[m.family] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-2.5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <Label className="flex items-center gap-1.5 text-[12.5px]"><Cpu className="h-3.5 w-3.5 text-primary" /> AI model</Label>
          <p className="mt-0.5 text-[10.5px] text-muted-foreground">
            Pick the model used for chapter drafting, chat, outlines, and analysis on this project.
          </p>
        </div>
        <div className="shrink-0">
          <Select value={currentId} onValueChange={pick} disabled={busy || !models.length}>
            <SelectTrigger className="h-8 w-72 text-[12px]" data-testid="settings-model-trigger">
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(families).map(([family, items]) => (
                <div key={family}>
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">{family}</div>
                  {items.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`settings-model-option-${m.id}`}>
                      <span className="font-medium">{m.label}</span>
                      <CostBadge tier={m.cost_tier} mult={m.cost_multiplier} />
                      <span className="ml-1.5 text-muted-foreground">— {m.recommended_for}</span>
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {active && (
        <div className="rounded-md border border-border bg-panel-header/30 px-3 py-2 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground">Ask {providerHandle(active)}</span>
            <span className="text-[10px]">·</span>
            <span className="font-mono-stat">{providerTagline(active)}</span>
            <span className="text-[10px]">·</span>
            <span>{active.recommended_for}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-sm bg-secondary px-1.5 py-0.5 font-mono-stat text-[10px] text-foreground">
              <DollarSign className="h-3 w-3" />
              {fmtUsd(pricePer10K(active))}/10K words
            </span>
            <span className="inline-flex items-center gap-1 rounded-sm bg-secondary px-1.5 py-0.5 font-mono-stat text-[10px] text-foreground">
              {fmtUsd(pricePer500K(active))}/500K words
            </span>
            {DETECTION_SCORES[active.id] != null && (
              <span className={cn(
                'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono-stat text-[10px]',
                DETECTION_SCORES[active.id] >= 70 ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'
                : DETECTION_SCORES[active.id] >= 60 ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                : 'bg-red-100 text-red-900 dark:bg-red-950/40 dark:text-red-200'
              )}>
                Detection evasion: {DETECTION_SCORES[active.id]}/100
              </span>
            )}
          </div>
          {BEST_USE[active.id] && (
            <div className="mt-1.5 text-[10.5px]">{BEST_USE[active.id]}</div>
          )}
        </div>
      )}

      {active && PREMIUM_METERED_IDS.includes(active.id) && (
        <div className="rounded-md border border-amber-300 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-900 dark:border-amber-800/40 dark:bg-amber-950/20 dark:text-amber-200"
             data-testid="model-picker-premium-gate">
          <div className="mb-0.5 flex items-center gap-1.5 font-semibold">
            <Lock className="h-3 w-3" />
            Premium model — PAYG credits required on Studio tier
          </div>
          <p>
            On the Studio tier ($35 / mo), {active.label} is metered against your
            PAYG word-credit balance. Standard models (GPT-5.2, Sonnet 4.5,
            Gemini Flash / Pro) are included in the tier at no extra charge.
          </p>
          <button
            type="button"
            onClick={() => setBuyCreditsOpen(true)}
            data-testid="model-picker-buy-credits"
            className="mt-2 inline-flex items-center gap-1 rounded-sm bg-amber-200 px-2 py-0.5 text-[10.5px] font-semibold text-amber-900 hover:bg-amber-300 dark:bg-amber-900/40 dark:text-amber-100"
          >
            <Coins className="h-3 w-3" /> Buy word credits
          </button>
        </div>
      )}

      <WordCreditBuyDialog open={buyCreditsOpen} onOpenChange={setBuyCreditsOpen} />

      <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-[10.5px] text-emerald-900 dark:border-emerald-800/40 dark:bg-emerald-950/20 dark:text-emerald-200"
           data-testid="model-mix-match-blurb">
        <div className="mb-0.5 flex items-center gap-1.5 font-semibold">
          <Sparkles className="h-3 w-3" />
          Mix &amp; Match freedom — always priced live
        </div>
        <p>
          Switch models anytime on any project — chapters can even span two
          models (draft on OPi, polish on Claude) so you spend only where
          quality matters. The price above updates the second you pick, so
          your running cost is never a surprise.
        </p>
        {active?.provider === 'openai' && (
          <p className="mt-1 rounded bg-white/50 px-1.5 py-1 text-[9.5px] dark:bg-black/20">
            <b>OPi bonus:</b> OpenAI caches your Series Bible + system prompt
            automatically after the first draft, cutting repeated-context
            input tokens by up to 90% on chapters 2..N.
          </p>
        )}
      </div>

      <ProjectBudgetControl />
    </div>
  );
}

function ProjectBudgetControl() {
  const { project, updateProject } = usePW();
  const [draft, setDraft] = useState('');
  const [budget, setBudget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!project) return;
    try {
      const r = await import('@/lib/api').then((m) => m.budgetApi.project(project.id));
      setBudget(r);
      setDraft(r.cap_usd != null ? String(r.cap_usd) : '');
    } catch (e) {
      // silent — endpoint optional in offline mode
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [project?.id]);

  if (!project) return null;

  const save = async () => {
    const v = draft.trim();
    const value = v === '' ? null : parseFloat(v);
    if (v !== '' && (Number.isNaN(value) || value < 0)) return toast.error('Enter a non-negative number or leave blank to inherit.');
    setBusy(true);
    try {
      await updateProject({ monthly_budget_usd: value });
      toast.success(value == null ? 'Using admin default budget.' : `Project budget set to $${value.toFixed(2)}/month.`);
      load();
    } catch (e) {
      toast.error(`Could not save budget: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const spent = budget?.spent_usd ?? 0;
  const cap = budget?.cap_usd;
  const pct = cap ? Math.min(100, Math.round((spent / cap) * 100)) : 0;

  return (
    <div className="rounded-md border border-border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[12.5px] font-semibold">AI budget cap — this project</div>
          <div className="text-[10.5px] text-muted-foreground">Block AI generation once this month&apos;s spend hits the cap. Blank = inherit admin default.</div>
        </div>
        <div className="text-right font-mono-stat text-[11px]">
          <div>${spent.toFixed(2)}{cap != null ? ` / $${Number(cap).toFixed(2)}` : ' (no cap)'}</div>
          {cap != null && (
            <div className="mt-0.5 h-1.5 w-32 overflow-hidden rounded-sm bg-secondary">
              <div className={cn('h-full transition-all', pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary')} style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <input
          data-testid="settings-budget-input"
          type="number"
          min="0"
          step="0.01"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={budget?.cap_usd != null ? '' : 'No cap (inherit admin)'}
          className="h-8 w-32 rounded-sm border border-border bg-background px-2 font-mono-stat text-[11.5px]"
        />
        <button
          data-testid="settings-budget-save"
          onClick={save}
          disabled={busy}
          className="h-8 rounded-sm bg-primary px-3 text-[11.5px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >Save cap</button>
      </div>
    </div>
  );
}

function CostBadge({ tier, mult }) {
  if (!tier) return null;
  const styles = {
    economy:  'bg-success/15 text-success',
    standard: 'bg-secondary text-foreground/80',
    premium:  'bg-primary/15 text-primary',
  }[tier] || 'bg-secondary text-foreground/80';
  return (
    <span className={cn('ml-1.5 inline-flex items-center rounded-sm px-1 py-0 font-mono-stat text-[9.5px] uppercase tracking-wider', styles)}>
      {tier}{mult ? ` · ${mult.toFixed(1)}×` : ''}
    </span>
  );
}
